import { setPasswordApi } from "@/api/services/main/auth";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Linkable } from "@/components/elements/Linkable";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { useValidateToken } from "@/features/auth/hooks/useValidateToken";
import { registerSchema } from "@/features/auth/schema/registerSchema";
import { TRegisterForm } from "@/features/auth/types/registerForm";
import { TRegisterQuery } from "@/features/auth/types/registerQuery";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { getQueryFromURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

// Cooldown period: 3 seconds between submissions
const SUBMISSION_COOLDOWN_MS = 3000;

const initForm: TRegisterForm = {
  email: "",
  ownerId: "",
  newPassword: "",
  confirmNewPassword: "",
};

export const RegisterForm = () => {
  const query = getQueryFromURL<TRegisterQuery>();
  const navigate = useNavigate();
  const [agree, setAgree] = useState(false);
  const isSubmittingRef = useRef(false);
  const lastSubmitTimeRef = useRef<number>(0);
  const [isCooldown, setIsCooldown] = useState(false);

  const { isValidatingToken, isTokenValid } = useValidateToken({
    resetToken: query?.resetToken,
    email: query?.email,
    ownerId: query?.ownerId,
  });

  const methods = useForm<TRegisterForm>({
    mode: "onChange",
    defaultValues: { ...initForm },
    resolver: yupResolver(registerSchema) as Resolver<TRegisterForm>,
  });
  const { control, formState } = methods;

  useEffect(() => {
    if (query) {
      const { email, ownerId } = query;
      if (email) {
        methods.setValue("email", email);
      }
      if (email) {
        methods.setValue("ownerId", ownerId);
      }
    }
  }, [query]);

  const onSubmit = async (data: TRegisterForm) => {
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTimeRef.current;

    // Prevent double submission
    if (isSubmittingRef.current) {
      return;
    }

    // Prevent rapid clicks (cooldown period)
    if (timeSinceLastSubmit < SUBMISSION_COOLDOWN_MS) {
      const remainingTime = Math.ceil((SUBMISSION_COOLDOWN_MS - timeSinceLastSubmit) / 1000);
      showError(new Error(`送信後、${remainingTime}秒待ってから再度お試しください。`));
      return;
    }

    const { newPassword } = data;
    if (!query) return;
    const { email, resetToken, ownerId } = query;
    
    isSubmittingRef.current = true;
    lastSubmitTimeRef.current = now;
    setIsCooldown(true);

    try {
      if (email && resetToken) {
        await setPasswordApi({ email, newPassword, resetToken, ownerId });
        const params = new URLSearchParams({
          email,
          ownerId,
        });
        navigate(`${AppRoutes.registerComplete}?${params.toString()}`);
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
      isSubmittingRef.current = false; // Reset on error to allow retry
      setIsCooldown(false);
      
      // Reset cooldown after error, but still enforce minimum delay
      setTimeout(() => {
        lastSubmitTimeRef.current = 0;
      }, SUBMISSION_COOLDOWN_MS);
    }
  };

  const isDisabled = formState.isSubmitting || isSubmittingRef.current || isCooldown;

  if (isValidatingToken) {
    return <LoadingScreen />;
  }

  if (!isTokenValid) {
    return null;
  }

  return (
    <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={registerSchema}>
      <Stack gap={3} width={400}>
        <FormField control={control} name="ownerId">
          <Input size="lg" disabled readonly />
        </FormField>
        <FormField control={control} name="email">
          <Input type="email" size="lg" disabled readonly />
        </FormField>
        <FormField control={control} name="newPassword">
          <Input type="password" size="lg" disabled={isDisabled} />
        </FormField>
        <FormField control={control} name="confirmNewPassword">
          <Input type="password" size="lg" disabled={isDisabled} />
        </FormField>
        <Typography variant="body14" color={"#5A6063"}>
          ※パスワードはアルファベット大文字・小文字・数字・記号を含む12文字以上で入力してください。
        </Typography>
        <Checkbox
          label={
            <Stack direction={"row"}>
              <Linkable href="https://iro-hana.com/privacy/" color="#1890FF" target="_blank" sx={{ textDecoration: "underline" }}>
                プライバシーポリシー
              </Linkable>
              に同意します。
            </Stack>
          }
          onChange={(e) => setAgree(e.target.checked)}
        />
        <Button 
          type="submit" 
          variant="primary" 
          fullWidth 
          disabled={!agree || isDisabled}
        >
          会員登録する
        </Button>
      </Stack>
    </Form>
  );
};
